import React, { useEffect, useRef, useState } from "react";

import { CircularIndeterminate, FormButton } from "components";
import { FormikSelectInput, FormikTextInput } from "components/UI/Inputs/FormikInputs";

import { Form, Formik, useFormikContext } from "formik";
import * as yup from "yup";

import { useRecoilValue, useSetRecoilState } from "recoil";
import {
    rowSelected,
    snackbarData,
    userCredentials,
} from "recoilState/GlobalState";

import { FORM_ERROR_MESSAGES } from "consts/errorMessages";

import styles from "styles/pages/ModalActions.module.scss";
import { handleResponse } from "services/Local-services";
import { migraPasoIns, migraTipoDePasoGet } from "services/services-mia/migra";
import { accesoGet } from "services/services-mia/miakey";

function AutoNombre({ lugares }) {
    const { values, setFieldValue } = useFormikContext();
    const prevLugarNombreRef = useRef(null);
    const nombreRef = useRef(values.migraPasoNombre);
    nombreRef.current = values.migraPasoNombre;

    useEffect(() => {
        const lugar = lugares.find(
            (l) => String(l.gateLugarId) === String(values.gateLugarId)
        );
        if (!lugar) return;
        const shouldUpdate =
            !nombreRef.current ||
            nombreRef.current === prevLugarNombreRef.current;
        if (shouldUpdate) setFieldValue("migraPasoNombre", lugar.gateLugarNombre);
        prevLugarNombreRef.current = lugar.gateLugarNombre;
    }, [values.gateLugarId, lugares, setFieldValue]);
    return null;
}

export default function InsertModal({ updateList, closeModal }) {
    const setSelected = useSetRecoilState(rowSelected);
    const setSnackbarInfo = useSetRecoilState(snackbarData);
    const credentials = useRecoilValue(userCredentials);

    const [inRequest, setInRequest] = useState(false);
    const [loadingOpts, setLoadingOpts] = useState(true);
    const [lugares, setLugares] = useState([]);
    const [tiposPaso, setTiposPaso] = useState([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoadingOpts(true);
            try {
                const [lugRes, tiposRes] = await Promise.all([
                    accesoGet({ incluirLugaresGlobales: "N" }, credentials),
                    migraTipoDePasoGet({}, credentials),
                ]);
                if (!cancelled) {
                    setLugares(lugRes.result || []);
                    setTiposPaso(tiposRes.result || []);
                }
            } finally {
                if (!cancelled) setLoadingOpts(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [credentials]);

    const handleSubmit = async ({
        migraPasoCod,
        migraPasoNombre,
        gateLugarId,
        migraTipoDePasoId,
    }) => {
        setInRequest(true);

        const cod =
            typeof migraPasoCod === "string"
                ? migraPasoCod.trim() || null
                : null;

        const nombre =
            typeof migraPasoNombre === "string"
                ? migraPasoNombre.trim() || null
                : null;

        const response = await migraPasoIns(
            {
                migraPasoCod: cod,
                migraPasoNombre: nombre,
                gateLugarId: Number(gateLugarId),
                migraTipoDePasoId: Number(migraTipoDePasoId),
            },
            credentials
        );

        handleResponse(response, "Paso añadido exitosamente", {
            setSnackbarInfo,
            setIsOpen: closeModal,
            setSelected,
            updateList,
        });

        setInRequest(false);
    };

    return (
        <Formik
            enableReinitialize
            initialValues={{
                migraPasoCod: "",
                migraPasoNombre: "",
                gateLugarId: lugares[0]?.gateLugarId ?? "",
                migraTipoDePasoId: tiposPaso[0]?.migraTipoDePasoId ?? "",
            }}
            validationSchema={yup.object({
                migraPasoCod: yup
                    .string(FORM_ERROR_MESSAGES.text)
                    .trim()
                    .max(50, "Máximo 50 caracteres")
                    .nullable(),
                migraPasoNombre: yup
                    .string(FORM_ERROR_MESSAGES.text)
                    .trim()
                    .max(150, "Máximo 150 caracteres")
                    .nullable(),
                gateLugarId: yup
                    .mixed()
                    .required(FORM_ERROR_MESSAGES.required)
                    .test(
                        "num",
                        FORM_ERROR_MESSAGES.number,
                        (v) => v !== "" && v != null && !Number.isNaN(Number(v))
                    ),
                migraTipoDePasoId: yup
                    .mixed()
                    .required(FORM_ERROR_MESSAGES.required)
                    .test(
                        "num",
                        FORM_ERROR_MESSAGES.number,
                        (v) => v !== "" && v != null && !Number.isNaN(Number(v))
                    ),
            })}
            onSubmit={(values, { setSubmitting }) => {
                try {
                    handleSubmit(values);
                } catch (e) {
                    setSnackbarInfo({
                        message: e.errmsg,
                        severity: "error",
                        open: true,
                    });
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            <Form className={styles.inputs} noValidate>
                <AutoNombre lugares={lugares} />
                <div className={styles["column-sm"]}>
                    {loadingOpts ? (
                        <CircularIndeterminate />
                    ) : (
                        <>
                            <FormikTextInput
                                fullWidth={true}
                                name="migraPasoCod"
                                labelText="Código"
                            />
                            <FormikTextInput
                                fullWidth={true}
                                name="migraPasoNombre"
                                labelText="Nombre"
                            />
                            <FormikSelectInput
                                name="gateLugarId"
                                labelText="Lugar"
                                disabled={lugares.length === 0}
                            >
                                {lugares.length === 0 ? (
                                    <option value="">
                                        No hay lugares disponibles
                                    </option>
                                ) : (
                                    lugares.map(
                                        ({
                                            gateLugarId,
                                            gateLugarNombre,
                                        }) => (
                                            <option
                                                key={gateLugarId}
                                                value={gateLugarId}
                                            >
                                                {gateLugarNombre}
                                            </option>
                                        )
                                    )
                                )}
                            </FormikSelectInput>
                            <FormikSelectInput
                                name="migraTipoDePasoId"
                                labelText="Tipo de paso"
                                disabled={tiposPaso.length === 0}
                            >
                                {tiposPaso.length === 0 ? (
                                    <option value="">
                                        No hay tipos de paso
                                    </option>
                                ) : (
                                    tiposPaso.map(
                                        ({
                                            migraTipoDePasoId,
                                            migraTipoDePasoNombre,
                                        }) => (
                                            <option
                                                key={migraTipoDePasoId}
                                                value={migraTipoDePasoId}
                                            >
                                                {migraTipoDePasoNombre}
                                            </option>
                                        )
                                    )
                                )}
                            </FormikSelectInput>
                            <FormButton
                                inRequest={inRequest}
                                newFormData={{ button: "Agregar" }}
                                onCloseModal={closeModal}
                            />
                        </>
                    )}
                </div>
            </Form>
        </Formik>
    );
}
